/**
 * <s-quote-form> — live quote estimate for the custom order enquiry form.
 *
 * Reads merchant pricing from a JSON config and option data-* attributes, shows only the
 * fields relevant to the selected order type, and recalculates on every change. Any price
 * the selection needs that the merchant has not set (blank) turns the total into
 * "Quote to be confirmed" so no unreliable figure is shown. Hidden fields are disabled so
 * native validation only checks what the customer can see. The estimate is copied into
 * hidden contact fields (emailed with the enquiry) and into sessionStorage so the
 * confirmation screen can repeat it after the native form post reloads the page.
 */
const STORAGE_KEY = 'stable-bakes-quote';

/** @param {string | undefined | null} value */
function toPrice(value) {
  if (value === undefined || value === null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

class SQuoteForm extends HTMLElement {
  connectedCallback() {
    const configEl = this.querySelector('[data-quote-config]');
    this.config = configEl ? JSON.parse(configEl.textContent || '{}') : {};
    this.strings = this.config.strings || {};
    this.money = new Intl.NumberFormat('en-AU', { style: 'currency', currency: this.config.currency || 'AUD' });
    this.form = this.querySelector('form');

    this.restoreSuccess();
    if (!this.form || !this.querySelector('[data-quote-total]')) return;

    this.handleChange = () => this.update();
    this.handleSubmit = () => this.persist();
    this.form.addEventListener('input', this.handleChange);
    this.form.addEventListener('change', this.handleChange);
    this.form.addEventListener('submit', this.handleSubmit);
    this.update();
  }

  disconnectedCallback() {
    if (!this.form || !this.handleChange) return;
    this.form.removeEventListener('input', this.handleChange);
    this.form.removeEventListener('change', this.handleChange);
    this.form.removeEventListener('submit', this.handleSubmit);
  }

  /** @param {string} selector */
  selectedOption(selector) {
    const select = /** @type {HTMLSelectElement | null} */ (this.querySelector(selector));
    if (!select || select.disabled || select.selectedIndex < 0) return null;
    const option = select.options[select.selectedIndex];
    return option && option.value !== '' ? option : null;
  }

  /** Show only the groups relevant to the current selections and disable hidden inputs. */
  toggleGroups(kind, method) {
    this.querySelectorAll('[data-show-for]').forEach((group) => {
      const kinds = (group.getAttribute('data-show-for') || '').split(' ');
      this.setGroupVisible(group, kinds.includes(kind));
    });
    this.querySelectorAll('[data-show-delivery]').forEach((group) => {
      this.setGroupVisible(group, method === 'delivery');
    });
  }

  setGroupVisible(group, visible) {
    group.hidden = !visible;
    group.querySelectorAll('input, select, textarea').forEach((field) => {
      field.disabled = !visible;
    });
  }

  compute() {
    const typeInput = this.querySelector('input[data-kind]:checked');
    const methodInput = this.querySelector('input[data-method]:checked');
    const kind = typeInput ? typeInput.dataset.kind : '';
    const method = methodInput ? methodInput.dataset.method : '';
    this.toggleGroups(kind, method);

    if (!typeInput) return null;

    const s = this.strings;
    const lines = [];
    const reasons = new Set();
    let tbc = false;
    let total = 0;
    const qtyInput = /** @type {HTMLInputElement | null} */ (this.querySelector('#co-quantity'));
    const quantity = Math.max(1, Math.floor(Number(qtyInput && qtyInput.value) || 1));
    const typeLabel = typeInput.value;

    /** Adds a priced line, or a "to be confirmed" line when the price is not set. */
    const add = (label, unitPrice, multiplier = 1, reason = 'pricing') => {
      if (unitPrice === null) {
        tbc = true;
        reasons.add(reason);
        lines.push({ label, text: s.tbcLine });
        return;
      }
      const amount = unitPrice * multiplier;
      total += amount;
      lines.push({ label, text: this.money.format(amount) });
    };

    if (kind === 'other') {
      tbc = true;
      reasons.add('other');
      lines.push({ label: typeLabel, text: s.tbcLine });
    } else if (kind === 'cake') {
      const size = this.selectedOption('#co-size');
      if (size) {
        const suffix = quantity > 1 ? ` × ${quantity}` : '';
        add(`${s.base}: ${typeLabel}, ${size.value}${suffix}`, toPrice(size.dataset.price), quantity);

        const tiersSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('#co-tiers'));
        const tiers = Number(tiersSelect && tiersSelect.value) || 1;
        if (tiers > 1) add(`${s.extraTiers} (${tiers - 1})${suffix}`, toPrice(this.config.extraTier), (tiers - 1) * quantity);

        const flavour = this.selectedOption('#co-flavour');
        if (flavour && flavour.dataset.premium === 'true') add(`${s.premiumFlavour}${suffix}`, toPrice(this.config.premiumFlavour), quantity);

        const filling = this.selectedOption('#co-filling');
        if (filling && filling.dataset.premium === 'true') add(`${s.premiumFilling}${suffix}`, toPrice(this.config.premiumFilling), quantity);

        const decoration = this.selectedOption('#co-decoration-level');
        const level = decoration ? decoration.dataset.level : 'simple';
        if (level === 'detailed') add(`${s.decoration}: ${decoration.value}${suffix}`, toPrice(this.config.decorationDetailed), quantity);
        if (level === 'showstopper') add(`${s.decoration}: ${decoration.value}`, null, 1, 'custom');
      } else {
        lines.push({ label: s.choose, text: '' });
        tbc = true;
      }
    } else {
      const unit = toPrice((this.config.unit || {})[kind]);
      add(`${typeLabel} × ${quantity}`, unit, quantity);
    }

    this.querySelectorAll('input[data-addon-label]:checked').forEach((addon) => {
      if (addon.disabled) return;
      add(addon.dataset.addonLabel, toPrice(addon.dataset.price));
    });

    if (method === 'delivery') {
      const zone = this.selectedOption('#co-zone');
      if (zone) {
        if (zone.dataset.zoneOther === 'true') add(`${s.delivery}: ${zone.value}`, null, 1, 'area');
        else add(`${s.delivery}: ${zone.value}`, toPrice(zone.dataset.fee));
      }
    }

    return { lines, tbc, total, reasons: [...reasons] };
  }

  update() {
    const result = this.compute();
    const list = this.querySelector('[data-quote-lines]');
    const totalEl = this.querySelector('[data-quote-total]');
    const reasonEl = this.querySelector('[data-quote-reason]');
    const totalField = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-quote-field-total]'));
    const linesField = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-quote-field-lines]'));
    if (!list || !totalEl) return;

    if (!result) {
      totalEl.textContent = '—';
      if (reasonEl) reasonEl.hidden = true;
      return;
    }

    list.replaceChildren(
      ...result.lines.map((line) => {
        const item = document.createElement('li');
        item.className = 'flex items-baseline justify-between gap-4';
        const label = document.createElement('span');
        label.textContent = line.label;
        const value = document.createElement('span');
        value.className = 'shrink-0 font-bold';
        value.textContent = line.text;
        item.append(label, value);
        return item;
      })
    );

    const totalText = result.tbc ? this.strings.tbc : this.money.format(result.total);
    totalEl.textContent = totalText;

    const reasonMap = {
      custom: this.strings.reasonCustom,
      pricing: this.strings.reasonPricing,
      area: this.strings.reasonArea,
      other: this.strings.reasonOther,
    };
    const reasonText = result.reasons.map((key) => reasonMap[key]).filter(Boolean).join(' ');
    if (reasonEl) {
      reasonEl.textContent = reasonText;
      reasonEl.hidden = !reasonText;
    }

    const breakdown = result.lines.map((line) => `${line.label}: ${line.text}`).join(' | ');
    if (totalField) totalField.value = totalText;
    if (linesField) linesField.value = breakdown;
    this.latest = { total: totalText, lines: result.lines.map((line) => `${line.label}: ${line.text}`) };
  }

  persist() {
    try {
      if (this.latest) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.latest));
    } catch (error) {
      /* Storage can be unavailable (private mode); the email still carries the estimate. */
    }
  }

  restoreSuccess() {
    const success = this.querySelector('[data-quote-success]');
    if (!success) return;
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      saved = null;
    }
    const box = success.querySelector('[data-quote-success-estimate]');
    if (saved && box) {
      const totalEl = box.querySelector('[data-quote-success-total]');
      const linesEl = box.querySelector('[data-quote-success-lines]');
      if (totalEl) totalEl.textContent = saved.total;
      if (linesEl) {
        linesEl.replaceChildren(
          ...saved.lines.map((text) => {
            const item = document.createElement('li');
            item.textContent = text;
            return item;
          })
        );
      }
      box.hidden = false;
    }
    if (success instanceof HTMLElement) success.focus();
  }
}

if (!customElements.get('s-quote-form')) customElements.define('s-quote-form', SQuoteForm);
